<template>
  <v-dialog v-model="showModal" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Create New User</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="formRef">
          <v-text-field
            v-model="formData.email"
            label="Email"
            type="email"
            variant="outlined"
            :rules="[
              v => !!v || 'Email is required',
              v => /.+@.+\..+/.test(v) || 'Email must be valid'
            ]"
            required
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="formData.first_name"
            label="First Name"
            variant="outlined"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="formData.last_name"
            label="Last Name"
            variant="outlined"
            class="mb-2"
          ></v-text-field>

          <v-select
            v-model="formData.school"
            :items="schoolOptions"
            label="School"
            variant="outlined"
            clearable
            class="mb-2"
          ></v-select>

          <v-select
            v-model="formData.role"
            :items="roleOptions"
            label="Role"
            variant="outlined"
            :rules="[v => !!v || 'Role is required']"
            required
            class="mb-2"
          ></v-select>

          <v-text-field
            v-model="formData.password"
            label="Password"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            :rules="[
              v => !!v || 'Password is required',
              v => v.length >= 8 || 'Password must be at least 8 characters'
            ]"
            required
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="formData.confirmPassword"
            label="Confirm Password"
            :type="showConfirmPassword ? 'text' : 'password'"
            variant="outlined"
            :rules="[
              v => !!v || 'Please confirm password',
              v => v === formData.password || 'Passwords must match'
            ]"
            required
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
          ></v-text-field>

          <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
            {{ error }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="closeModal" :disabled="submitting">
          Cancel
        </v-btn>
        <v-btn color="primary" @click="submitUser" :loading="submitting">
          Create User
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/store/auth'

const authStore = useAuthStore()

interface School {
  id: string
  name: string
}

interface Props {
  show: boolean
  schools: School[]
  submitting?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  submitting: false,
  error: ''
})

const emit = defineEmits(['close', 'submit'])

const formRef = ref<any>(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const formData = ref({
  email: '',
  first_name: '',
  last_name: '',
  organization: '',
  school: null as string | null,
  role: 'user',
  password: '',
  confirmPassword: ''
})

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const schoolOptions = computed(() => {
  return props.schools.map(school => ({
    title: school.name,
    value: school.id
  }))
})

// Get available roles from auth store with disabled state for roles above current user
const roleOptions = computed(() => {
  // Use the perceived role (authUser.role, which is overridden during "view as")
  const currentRole = authStore.authUser.role
  const currentUserLevel = authStore.roleHierarchy[currentRole] || 0

  // Get all roles from hierarchy
  const allRoles = Object.keys(authStore.roleHierarchy)
    .filter(role => role !== 'pending' && role !== 'root') // Exclude pending and root from options
    .map(role => {
      const roleLevel = authStore.roleHierarchy[role] || 0
      return {
        title: authStore.roleDisplayNames[role] || role,
        value: role,
        disabled: roleLevel > currentUserLevel // Disable roles above current user's level
      }
    })
    .sort((a, b) => {
      const levelA = authStore.roleHierarchy[a.value] || 0
      const levelB = authStore.roleHierarchy[b.value] || 0
      return levelB - levelA // Sort by level descending
    })

  return allRoles
})

const closeModal = () => {
  formData.value = {
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    school: null,
    role: 'user',
    password: '',
    confirmPassword: ''
  }
  showPassword.value = false
  showConfirmPassword.value = false
  emit('close')
}

const submitUser = async () => {
  const { valid } = await formRef.value.validate()

  if (!valid) {
    return
  }

  const userData = {
    email: formData.value.email,
    first_name: formData.value.first_name,
    last_name: formData.value.last_name,
    organization: formData.value.organization,
    school: formData.value.school,
    role: formData.value.role,
    password: formData.value.password
  }

  emit('submit', userData)
}

// Reset form when modal closes
watch(() => props.show, (newValue) => {
  if (!newValue) {
    formRef.value?.reset()
  }
})
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
